/**
 * Journée festivalière : la date à laquelle un festivalier rattache un concert,
 * qui n'est pas la date calendaire.
 *
 * Un set à 01h00 appartient à la nuit de la veille. C'est le piège classique des
 * grilles de festival : sans cette notion, la programmation du vendredi soir se
 * scinde en deux dans l'interface, et les concerts de fin de nuit basculent au
 * lendemain.
 *
 * La bascule dépend de deux paramètres portés par le festival : son fuseau
 * horaire et l'heure à laquelle sa journée commence (INTERNATIONAL.md §1.2).
 */

/** Date locale au format ISO `YYYY-MM-DD`, sans notion d'heure ni de fuseau. */
export type FestivalDay = string;

type LocalParts = { year: number; month: number; day: number; hour: number };

/**
 * Décompose un instant dans un fuseau donné.
 *
 * `Intl` est le seul moyen fiable de le faire sans dépendance : il connaît les
 * changements d'heure, qu'un décalage fixe manquerait.
 */
function partsInTimezone(instant: Date, timezone: string): LocalParts {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
  });

  const values: Record<string, string> = {};
  for (const part of formatter.formatToParts(instant)) {
    if (part.type !== "literal") values[part.type] = part.value;
  }

  return {
    year: Number(values["year"]),
    month: Number(values["month"]),
    day: Number(values["day"]),
    hour: Number(values["hour"]),
  };
}

function toIsoDate({ year, month, day }: Omit<LocalParts, "hour">): FestivalDay {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${year}-${pad(month)}-${pad(day)}`;
}

/**
 * Journée festivalière à laquelle appartient un instant.
 *
 * @param instant       Moment du concert, en UTC.
 * @param timezone      Fuseau IANA du site, par exemple « Europe/Paris ».
 * @param dayStartHour  Heure locale de bascule, en heures pleines (souvent 6).
 */
export function festivalDayOf(
  instant: Date,
  timezone: string,
  dayStartHour: number,
): FestivalDay {
  if (!Number.isInteger(dayStartHour) || dayStartHour < 0 || dayStartHour > 23) {
    throw new RangeError(
      `Heure de bascule invalide : ${dayStartHour} (attendu : entier de 0 à 23)`,
    );
  }

  const local = partsInTimezone(instant, timezone);
  if (local.hour >= dayStartHour) {
    return toIsoDate(local);
  }

  // Avant la bascule : le concert appartient à la veille. Le recul se fait sur la
  // date locale et non sur l'instant, sinon une nuit de changement d'heure — qui
  // ne dure pas 24 heures — donnerait le mauvais jour.
  const previous = new Date(Date.UTC(local.year, local.month - 1, local.day));
  previous.setUTCDate(previous.getUTCDate() - 1);

  return toIsoDate({
    year: previous.getUTCFullYear(),
    month: previous.getUTCMonth() + 1,
    day: previous.getUTCDate(),
  });
}

/**
 * Wall-clock time at the festival's place, as an instant.
 *
 * Organisers think and type in the site's local time ("20:00 on the main
 * stage"). Turning that into an instant needs the site's time zone, never the
 * server's nor the organiser's computer: a lineup typed in Paris for a festival
 * in Lisbon must not move by an hour.
 *
 * Around a change of time: an hour that does not exist (spring forward) lands
 * after the gap, an hour that exists twice (fall back) takes its first
 * occurrence.
 */
export function zonedTimeToInstant(
  date: string,
  hour: number,
  minute: number,
  timezone: string,
): Date {
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  const wallClock = Date.UTC(year, month - 1, day, hour, minute);

  // The offsets in force half a day before and after: any change of time near
  // this hour lies between them.
  const before = offsetMinutes(new Date(wallClock - 12 * 3_600_000), timezone);
  const after = offsetMinutes(new Date(wallClock + 12 * 3_600_000), timezone);

  // An instant is right when its own offset gives back the hour asked for.
  const matches = [before, after]
    .map((offset) => wallClock - offset * 60_000)
    .filter(
      (instant) =>
        wallClock - offsetMinutes(new Date(instant), timezone) * 60_000 === instant,
    );

  if (matches.length > 0) return new Date(Math.min(...matches));

  // The hour does not exist: read it with the offset from before the gap,
  // which lands after it (02:30 on a spring-forward night becomes 03:30).
  return new Date(wallClock - before * 60_000);
}

/** Offset of `timezone` from UTC at `instant`, in minutes (Paris in July: 120). */
function offsetMinutes(instant: Date, timezone: string): number {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const values: Record<string, number> = {};
  for (const part of formatter.formatToParts(instant)) {
    if (part.type !== "literal") values[part.type] = Number(part.value);
  }
  const asUtc = Date.UTC(
    values["year"] as number,
    (values["month"] as number) - 1,
    values["day"] as number,
    values["hour"] as number,
    values["minute"] as number,
    values["second"] as number,
  );
  return Math.round((asUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60_000);
}

/** An instant as the value of a `datetime-local` field, in the site's time. */
export function instantToZonedInput(instant: Date, timezone: string): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const values: Record<string, string> = {};
  for (const part of formatter.formatToParts(instant)) {
    if (part.type !== "literal") values[part.type] = part.value;
  }
  return `${values["year"]}-${values["month"]}-${values["day"]}T${values["hour"]}:${values["minute"]}`;
}

/** The value of a `datetime-local` field, read in the site's time. */
export function zonedInputToInstant(value: string, timezone: string): Date | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  const [, date, hours, minutes] = match as unknown as [string, string, string, string];
  return zonedTimeToInstant(date, Number(hours), Number(minutes), timezone);
}
